import React, { useEffect, useState } from "react";
import Button from "~/components/ui/button";
import { CustomCheckbox } from "~/components/ui/check_Box";
import styles from "../../index.module.scss";

interface AppOption {
  id: string;
  name: string;
  code: string;
}

interface FieldOption {
  id: string;
  name: string;
  type?: string; // フィールドタイプ情報を追加
}

interface FieldMapping {
  field: string;
  relatedField: string;
}

interface LookupConfig {
  noLabel?: boolean;
  required?: boolean;
  lookup?: {
    relatedApp: {
      app: string;
      code: string;
    };
    relatedKeyField: string;
    fieldMappings: FieldMapping[];
    lookupPickerFields: string[];
    sort: string;
  };
  [key: string]: any;
}

interface LookupOptionsProps {
  options: LookupConfig;
  onChange: (key: string, value: any) => void;
  getFieldsForApp: (appId: string) => any[];
  isReadOnly?: boolean;
  appList?: { app_id: string; app_name: string }[]; // アプリ一覧を外部から受け取る
  currentAppId?: string; // 現在のアプリIDを受け取る
}

// コピー元のキーとして許可するフィールドタイプ
const allowedRelatedKeyFieldTypes = ["SINGLE_LINE_TEXT", "NUMBER", "LINK", "RECORD_NUMBER"];

// 「ほかのフィールドのコピー」の制約マッピング
const allowedSourceFieldTypesMap: Record<string, string[]> = {
  // コピー先タイプ: [許可されるコピー元タイプ]
  SINGLE_LINE_TEXT: ["SINGLE_LINE_TEXT", "LOOKUP", "RADIO_BUTTON", "DROP_DOWN"],
  NUMBER: ["NUMBER", /*'CALC',*/ "LOOKUP", "RECORD_NUMBER"], // CALCは不明なためコメントアウト
  MULTI_LINE_TEXT: ["SINGLE_LINE_TEXT", "MULTI_LINE_TEXT", "LOOKUP"],
  RICH_TEXT: ["SINGLE_LINE_TEXT", "MULTI_LINE_TEXT", "RICH_TEXT", "LOOKUP"],
  RADIO_BUTTON: ["RADIO_BUTTON"],
  CHECK_BOX: ["CHECK_BOX"], // kintoneヘルプではCHECKBOXだが、コードに合わせてCHECK_BOX
  MULTI_SELECT: ["MULTI_SELECT"],
  DROP_DOWN: ["DROP_DOWN"],
  LINK: ["LINK"],
  DATE: ["DATE"],
  TIME: ["TIME"],
  DATETIME: ["DATETIME" /*'CREATED_TIME', 'UPDATED_TIME'*/], // Kintone固有フィールドは不明なためコメントアウト
  USER_SELECT: ["USER_SELECT" /*'CREATED_BY', 'MODIFIED_BY'*/], // Kintone固有フィールドは不明なためコメントアウト
  ORGANIZATION_SELECT: ["ORGANIZATION_SELECT"],
  GROUP_SELECT: ["GROUP_SELECT"],
};

const LookupOptions: React.FC<LookupOptionsProps> = ({
  options,
  onChange,
  getFieldsForApp,
  isReadOnly = false,
  appList = [],
  currentAppId: _currentAppId,
}) => {
  // 外部から受け取ったアプリ一覧を使用する
  const [appOptions, setAppOptions] = useState<AppOption[]>([]);

  // 現在のアプリのフィールド
  const [currentAppFields, setCurrentAppFields] = useState<FieldOption[]>([]);

  // 選択されたアプリのフィールド
  const [lookupAppFields, setLookupAppFields] = useState<FieldOption[]>([]);

  // 前回のlookupAppFieldsを追跡するref（不要な更新を防ぐ）
  const prevLookupAppFieldsRef = React.useRef<FieldOption[]>([]);

  // 参照先アプリのフィールド変更監視フラグ
  const [needsUpdate, setNeedsUpdate] = useState<boolean>(false);
  // --- 選択されたコピー先フィールドのStateを追加 --- START
  const [selectedTargetField, setSelectedTargetField] = useState<FieldOption | null>(null);
  const [filteredSourceFields, setFilteredSourceFields] = useState<FieldOption[]>([]);
  // --- 選択されたコピー先フィールドのStateを追加 --- END

  // 現在の設定値を取得（メモ化して参照の安定化）
  const lookup = React.useMemo(
    () =>
      options.lookup || {
        relatedApp: { app: "", code: "" },
        relatedKeyField: "",
        fieldMappings: [],
        lookupPickerFields: [],
        sort: "",
      },
    [options.lookup],
  );

  // 選択されているアプリのID
  const selectedAppId = lookup.relatedApp.app;

  // オプションのリスト
  const lookupOptions = [
    { key: "noLabel", label: "フィールド名を表示しない" },
    { key: "required", label: "必須項目にする" },
  ];

  // refを使って最新の値を保持（useCallbackの依存配列を安定させるため）
  const currentAppFieldsRef = React.useRef(currentAppFields);
  const lookupRef = React.useRef(lookup);

  // 値が変わったらrefを更新
  React.useEffect(() => {
    currentAppFieldsRef.current = currentAppFields;
  }, [currentAppFields]);

  React.useEffect(() => {
    lookupRef.current = lookup;
  }, [lookup]);

  // 参照先アプリのフィールド変更時に整合性を検証し更新する
  // refを使うことでコールバックの参照を安定化
  const validateAndUpdateReferences = React.useCallback(
    (fields: FieldOption[]) => {
      const currentLookup = lookupRef.current;
      const currentFields = currentAppFieldsRef.current;

      // 存在するフィールドIDリスト（参照先アプリ）
      const validFieldIds = fields.map((f) => f.id);

      // 存在するフィールドIDリスト（現在のアプリ）
      const validCurrentFieldIds = currentFields.map((f) => f.id);

      // コピー元フィールドの検証・更新
      let updatedRelatedKeyField = currentLookup.relatedKeyField;
      let shouldUpdateSettings = false;

      // コピー元フィールドが無効になった場合は削除し、関連するマッピングもすべて削除
      if (updatedRelatedKeyField && !validFieldIds.includes(updatedRelatedKeyField)) {
        updatedRelatedKeyField = "";
        // コピー元フィールドが無効になった場合は、すべてのマッピングを削除
        shouldUpdateSettings = true;
      }

      // フィールドマッピングの検証・更新
      // 1. コピー元フィールドが削除された場合はすべてのマッピングを削除
      // 2. 個別のマッピングで参照先フィールドまたは現在のアプリのフィールドが削除された場合は、そのマッピングを削除
      const updatedFieldMappings = shouldUpdateSettings
        ? [] // コピー元フィールドが削除された場合は全マッピング削除
        : currentLookup.fieldMappings.filter(
            (mapping) =>
              validFieldIds.includes(mapping.relatedField) &&
              validCurrentFieldIds.includes(mapping.field),
          );

      const updatedPickerFields = (currentLookup.lookupPickerFields ?? []).filter((id) =>
        validFieldIds.includes(id),
      );

      if (currentLookup.lookupPickerFields.length !== updatedPickerFields.length) {
        // TODO: エラーメッセージを表示
      }

      // ソート条件の検証・更新
      // 参照先アプリに存在しないフィールドによるソート条件は削除
      let updatedSort = "";
      if (currentLookup.sort) {
        const sortConditions = currentLookup.sort.split(",").map((item) => {
          const [field, order] = item.trim().split(" ");
          return { field, order };
        });
        const validSortConditions = sortConditions.filter((sort) =>
          validFieldIds.includes(sort.field!),
        );

        updatedSort = validSortConditions.map((sort) => `${sort.field} ${sort.order}`).join(", ");
      }

      // 変更があれば設定を更新
      // JSON.stringifyを使用して深い比較を行い、フィールドマッピングの内容に変更があるかを確認
      const hasFieldMappingsChanged =
        updatedFieldMappings.length !== currentLookup.fieldMappings.length ||
        JSON.stringify(updatedFieldMappings) !== JSON.stringify(currentLookup.fieldMappings);
      const hasPickerFieldsChanged =
        updatedPickerFields.length !== (currentLookup.lookupPickerFields ?? []).length ||
        JSON.stringify(updatedPickerFields) !==
          JSON.stringify(currentLookup.lookupPickerFields ?? []);
      if (
        updatedRelatedKeyField !== currentLookup.relatedKeyField ||
        hasFieldMappingsChanged ||
        hasPickerFieldsChanged ||
        updatedSort !== currentLookup.sort
      ) {
        // TODO: エラーメッセージを表示
        onChange("lookup", {
          ...currentLookup,
          relatedKeyField: updatedRelatedKeyField,
          fieldMappings: updatedFieldMappings,
          lookupPickerFields: updatedPickerFields,
          sort: updatedSort,
        });
      } else {
        // ... (エラーメッセージ表示のTODO)
      }
    },
    [onChange],
  );

  // アプリリストが更新されたら、アプリオプションを更新
  useEffect(() => {
    if (appList && appList.length > 0) {
      const newAppOptions = appList.map((app) => ({
        id: app.app_id,
        name: app.app_name,
        code: app.app_name.replace(/アプリ$/, ""), // アプリという文字を除去してコード化
      }));
      setAppOptions(newAppOptions);
    }
  }, [appList]);

  // 選択されたアプリが変更されたときに関連フィールドを更新
  useEffect(() => {
    if (selectedAppId) {
      // 実際のアプリのフィールド一覧を取得
      const rawFields = getFieldsForApp(selectedAppId);
      const fields = rawFields.map((field) => ({
        id: field.code,
        name: field.label,
        type: field.type,
      }));

      // フィールドリストが実際に変更されたかをチェック（参照ではなく内容で比較）
      const prevFieldIds = new Set(prevLookupAppFieldsRef.current.map((f) => f.id));
      const newFieldIds = new Set(fields.map((f) => f.id));
      const hasFieldsChanged =
        fields.length !== prevLookupAppFieldsRef.current.length ||
        fields.some((f) => !prevFieldIds.has(f.id)) ||
        prevLookupAppFieldsRef.current.some((f) => !newFieldIds.has(f.id));

      // 変更があった場合のみstateを更新
      if (hasFieldsChanged) {
        prevLookupAppFieldsRef.current = fields;
        setLookupAppFields(fields);
      }

      // 参照先アプリのフィールドが変更された場合、設定の整合性チェック
      if (needsUpdate) {
        validateAndUpdateReferences(fields);
        setNeedsUpdate(false);
      }
    } else {
      if (prevLookupAppFieldsRef.current.length > 0) {
        prevLookupAppFieldsRef.current = [];
        setLookupAppFields([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppId, getFieldsForApp, needsUpdate]);

  // 現在のアプリのフィールドを取得（親コンポーネントから取得）
  const currentAppId = options["currentAppId"] || "";

  // 現在のアプリのフィールドを追跡するためのref（useEffectの依存を避ける）
  const prevCurrentAppFieldsRef = React.useRef<FieldOption[]>([]);

  useEffect(() => {
    const fields = getFieldsForApp(currentAppId);
    const newFields = fields.map((field) => ({
      id: field.code,
      name: field.label,
      type: field.type,
    }));

    // フィールドリストが実際に変更されたかをチェック（参照ではなく内容で比較）
    const prevFieldIds = new Set(prevCurrentAppFieldsRef.current.map((f) => f.id));
    const newFieldIds = new Set(newFields.map((f) => f.id));
    const hasChanged =
      newFields.length !== prevCurrentAppFieldsRef.current.length ||
      newFields.some((f) => !prevFieldIds.has(f.id)) ||
      prevCurrentAppFieldsRef.current.some((f) => !newFieldIds.has(f.id));

    if (!hasChanged) {
      return; // 変更がなければ何もしない
    }

    // 削除されたフィールドがあるか確認
    const hasDeletedFields = prevCurrentAppFieldsRef.current.some(
      (field) => !newFieldIds.has(field.id),
    );

    // refを更新
    prevCurrentAppFieldsRef.current = newFields;
    setCurrentAppFields(newFields);

    // 現在のアプリのフィールドが変更され、かつ削除されたフィールドがある場合にのみ検証を実行
    // lookupAppFieldsはrefから取得して依存配列から除外
    const currentLookupFields = prevLookupAppFieldsRef.current;
    if (selectedAppId && currentLookupFields.length > 0 && hasDeletedFields) {
      validateAndUpdateReferences(currentLookupFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFieldsForApp, currentAppId, selectedAppId]);

  // --- コピー先フィールド選択時にコピー元選択肢をフィルタリング --- START
  useEffect(() => {
    if (selectedTargetField?.type) {
      const allowedTypes = allowedSourceFieldTypesMap[selectedTargetField.type] || [];
      const filtered = lookupAppFields.filter(
        (field) => field.type && allowedTypes.includes(field.type),
      );
      setFilteredSourceFields(filtered);
    } else {
      setFilteredSourceFields([]); // コピー先が選択されていない場合は空にする
    }
    // コピー先が変わったら、コピー元の選択もリセット
    const sourceFieldSelect = document.getElementById("sourceField") as HTMLSelectElement;
    if (sourceFieldSelect) {
      sourceFieldSelect.value = "";
    }
  }, [selectedTargetField, lookupAppFields]);
  // --- コピー先フィールド選択時にコピー元選択肢をフィルタリング --- END

  // moved earlier as useCallback

  const handleBooleanOptionChange = (key: string, checked: boolean) => {
    onChange(key, checked);
  };

  // ルックアップアプリ選択が変更された時の処理
  const handleLookupAppChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const appId = e.target.value;
    // 選択されたアプリの情報を取得
    const selectedApp = appOptions.find((app) => app.id === appId);
    const appCode = selectedApp?.code || "";

    // 新しいアプリを設定
    onChange("lookup", {
      ...lookup,
      relatedApp: { app: appId, code: appCode },
      relatedKeyField: "",
      fieldMappings: [],
      lookupPickerFields: [],
      sort: "",
    });
    setNeedsUpdate(true);
    setSelectedTargetField(null); // アプリが変わったらコピー先選択もリセット
  };

  // コピー元のフィールド選択が変更された時の処理
  const handleRelatedKeyFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const fieldId = e.target.value;
    onChange("lookup", {
      ...lookup,
      relatedKeyField: fieldId,
    });
  };

  const addFieldMapping = (field: string, relatedField: string) => {
    if (!field || !relatedField) return;
    const existingMapping = lookup.fieldMappings.find((mapping) => mapping.field === field);
    let newFieldMappings: FieldMapping[];
    if (existingMapping) {
      newFieldMappings = lookup.fieldMappings.map((mapping) =>
        mapping.field === field ? { field, relatedField } : mapping,
      );
    } else {
      // 新しいマッピングを追加
      newFieldMappings = [...lookup.fieldMappings, { field, relatedField }];
    }
    onChange("lookup", {
      ...lookup,
      fieldMappings: newFieldMappings,
    });
  };

  const removeFieldMapping = (field: string) => {
    const newFieldMappings = lookup.fieldMappings.filter((mapping) => mapping.field !== field);
    onChange("lookup", {
      ...lookup,
      fieldMappings: newFieldMappings,
    });
  };

  // ルックアップピッカーフィールドの選択が変更された時の処理
  const handleLookupPickerFieldChange = (fieldId: string, checked: boolean) => {
    let newPickerFields: string[];
    const currentPickerFields = lookup.lookupPickerFields ?? [];
    if (checked) {
      // 選択された場合は追加
      newPickerFields = [...currentPickerFields, fieldId];
    } else {
      // 選択解除された場合は削除
      newPickerFields = currentPickerFields.filter((id) => id !== fieldId);
    }
    onChange("lookup", {
      ...lookup,
      lookupPickerFields: newPickerFields,
    });
  };

  // ソート設定の変更処理
  const handleLookupSortChange = (fieldId: string, order: string) => {
    const sortItems = lookup.sort ? lookup.sort.split(",").map((item) => item.trim()) : [];
    const newSort = `${fieldId} ${order}`;
    const fieldExists = sortItems.findIndex((item) => item.startsWith(`${fieldId} `));
    if (fieldExists !== -1) {
      sortItems[fieldExists] = newSort;
    } else {
      sortItems.push(newSort);
    }
    onChange("lookup", {
      ...lookup,
      sort: sortItems.join(", "),
    });
  };

  // 現在のソート条件を解析
  const currentLookupSortConditions = lookup.sort
    ? lookup.sort.split(",").map((item) => {
        const [field, order] = item.trim().split(" ");
        return { field, order };
      })
    : [];

  const getAppName = (appId: string): string => {
    const app = appOptions.find((app) => String(app.id) === String(appId));
    return app ? app.name : "Unknown App";
  };

  // コピー元のキー選択肢をフィルタリング
  const filteredRelatedAppFieldsForKey = lookupAppFields.filter((field) =>
    allowedRelatedKeyFieldTypes.includes(field.type || ""),
  );

  // --- コピー先フィールド選択ハンドラ --- START
  const handleTargetFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const fieldId = e.target.value;
    const field = currentAppFields.find((f) => f.id === fieldId) || null;
    setSelectedTargetField(field);
  };
  // --- コピー先フィールド選択ハンドラ --- END

  return (
    <div className={styles["optionSection"]}>
      {/* チェックボックスオプション */}
      {lookupOptions.map((option) => (
        <div key={option.key} className={styles["form-group"]}>
          <CustomCheckbox
            label={option.label}
            checked={options[option.key] || false}
            onChange={(checked) => handleBooleanOptionChange(option.key, checked)}
            disabled={isReadOnly}
          />
        </div>
      ))}

      {/* 関連付けるアプリの選択 */}
      <div className={styles["form-group"]}>
        <label>関連付けるアプリ</label>
        <select
          value={selectedAppId}
          onChange={handleLookupAppChange}
          disabled={isReadOnly}
          className={styles["select-input"]}
        >
          <option value="">アプリを選択してください</option>
          {appOptions.map((app) => (
            <option
              key={app.id}
              value={app.id}
              disabled={app.id === options["currentAppId"]} // 編集中のアプリを選択できないようにする
            >
              {app.name} {app.id === options["currentAppId"] ? "(編集中のアプリ)" : ""}
            </option>
          ))}
        </select>
        {/* エラーメッセージ表示条件の修正 */}
        {selectedAppId && !appOptions.some((app) => String(app.id) === String(selectedAppId)) && (
          <div style={{ color: "red", marginTop: "5px" }}>
            選択されたアプリID: {selectedAppId} ({getAppName(selectedAppId)})
          </div>
        )}
      </div>

      {selectedAppId && (
        <>
          {/* コピー元のフィールド選択 */}
          <div className={styles["form-group"]}>
            <label>コピー元のフィールド（関連付けのキー）</label>
            <select
              value={lookup.relatedKeyField}
              onChange={handleRelatedKeyFieldChange}
              disabled={isReadOnly}
              className={styles["select-input"]}
            >
              <option value="">選択してください</option>
              {/* キー用のフィルタリングされたリストを使用 */}
              {filteredRelatedAppFieldsForKey.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.name} ({field.type})
                </option>
              ))}
            </select>
          </div>

          {/* フィールドマッピング */}
          <div className={styles["form-group"]}>
            <label>他のフィールドのコピー</label>
            <div className={styles["mapping-container"]}>
              {lookup.fieldMappings.map((mapping, index) => {
                const targetField = currentAppFields.find((f) => f.id === mapping.field);
                const sourceField = lookupAppFields.find((f) => f.id === mapping.relatedField);
                if (!targetField || !sourceField) return null;
                return (
                  <div key={index} className={styles["mapping-item"]}>
                    <span>{targetField.name}</span>
                    <span>←</span>
                    <span>{sourceField.name}</span>
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => removeFieldMapping(mapping.field)}
                        className={styles["remove-button"]}
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {!isReadOnly && (
              <div className={styles["add-mapping"]}>
                <div className={styles["mapping-controls"]}>
                  <select
                    id="targetField"
                    className={styles["select-input"]}
                    style={{ marginBottom: "10px" }}
                    onChange={handleTargetFieldChange} // onChangeハンドラを追加
                    value={selectedTargetField?.id || ""} // valueをstateにバインド
                  >
                    <option value="">コピー先のフィールドを選択</option>
                    {currentAppFields
                      .filter(
                        (field) =>
                          !lookup.fieldMappings.some((m) => m.field === field.id) &&
                          field.type !== "SUBTABLE", // テーブル自体はコピー先にできない
                      )
                      .map((field) => (
                        <option key={field.id} value={field.id}>
                          {field.name}
                        </option>
                      ))}
                  </select>

                  <div style={{ margin: "5px 0", textAlign: "center" }}>←</div>

                  <select
                    id="sourceField"
                    className={styles["select-input"]}
                    style={{ marginBottom: "10px" }}
                    disabled={!selectedTargetField} // コピー先が選択されるまで無効
                  >
                    <option value="">コピー元のフィールドを選択</option>
                    {filteredSourceFields.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.name}
                      </option>
                    ))}
                  </select>

                  <Button
                    label="追加"
                    onClick={() => {
                      const targetField = (
                        document.getElementById("targetField") as HTMLSelectElement
                      )?.value;
                      const sourceField = (
                        document.getElementById("sourceField") as HTMLSelectElement
                      )?.value;
                      if (targetField && sourceField) {
                        addFieldMapping(targetField, sourceField);
                        // セレクトボックスをリセット
                        if (document.getElementById("targetField") instanceof HTMLSelectElement) {
                          (document.getElementById("targetField") as HTMLSelectElement).value = "";
                        }
                        if (document.getElementById("sourceField") instanceof HTMLSelectElement) {
                          (document.getElementById("sourceField") as HTMLSelectElement).value = "";
                        }
                      }
                    }}
                    styleType="white"
                  />
                </div>
              </div>
            )}
          </div>

          {/* ルックアップ選択時に表示するフィールド */}
          <div className={styles["form-group"]}>
            <label>ルックアップ選択時に表示するフィールド</label>
            <div className={styles["fields-container"]}>
              {lookupAppFields.map((field) => (
                <div key={field.id} className={styles["field-checkbox"]}>
                  <CustomCheckbox
                    label={`${field.name}`}
                    checked={(lookup.lookupPickerFields ?? []).includes(field.id)}
                    onChange={(checked) => handleLookupPickerFieldChange(field.id, checked)}
                    disabled={isReadOnly}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* レコードの初期設定（ソート） */}
          <div className={styles["form-group"]}>
            <label>レコードの初期設定（ソート）</label>
            <div className={styles["sort-container"]}>
              {currentLookupSortConditions.length > 0 && (
                <div className={styles["current-sort"]}>
                  <p>現在のソート条件:</p>
                  <ul>
                    {currentLookupSortConditions.map((sort, index) => {
                      const field = lookupAppFields.find((f) => f.id === sort.field);
                      if (!field) return null;
                      const orderText = sort.order === "asc" ? "昇順" : "降順";
                      return (
                        <li key={index}>
                          {field.name} ({orderText})
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={() => {
                                const newSortItems = currentLookupSortConditions.filter(
                                  (_, i) => i !== index,
                                );
                                onChange("lookup", {
                                  ...lookup,
                                  sort: newSortItems.map((s) => `${s.field} ${s.order}`).join(", "),
                                });
                              }}
                              className={styles["remove-sort"]}
                            >
                              ×
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* 新しいソート条件の追加 */}
              {!isReadOnly && (
                <div className={styles["add-sort"]}>
                  <p>ソート条件を追加:</p>
                  <div className={styles["sort-field-container"]}>
                    <select
                      className={styles["select-input"]}
                      onChange={(e) => {
                        const fieldId = e.target.value;
                        if (fieldId) {
                          handleLookupSortChange(fieldId, "asc");
                          e.target.value = "";
                        }
                      }}
                    >
                      <option value="">ソートするフィールドを選択</option>
                      {lookupAppFields.map((field) => (
                        <option
                          key={field.id}
                          value={field.id}
                          disabled={currentLookupSortConditions.some((s) => s.field === field.id)}
                        >
                          {field.name}
                        </option>
                      ))}
                    </select>

                    <div className={styles["sort-order-buttons"]}>
                      {/* ソート順選択は最後に選択されたフィールドに対して行うのが自然か？別途UI検討 */}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LookupOptions;
